import { ChangeEvent, FC, FormEvent, useEffect, useRef, useState } from 'react'
import { AlertCircle, Check, CheckCircle } from "lucide-react"
import Link from "next/link"
import {
  getPriorityOptions,
  getPremiseTypes,
  getBuildingTypes,
  getDemolitionOptions,
  getDesignProjectOptions,
} from "@/src/utils/contactFormHelper"
import { ContactFormData, sendEmailAction } from "@/app/actions/email"
import Selector from "@/src/components/custom/Selector"
import { useLanguage } from "@/src/context/LanguageContext"
import { useUI } from "@/src/lib/i18n/ui"

const Form: FC = () => {
  const { lang } = useLanguage()
  const t = useUI(lang)

  const phoneDefault = lang === 'fr' ? '+33' : '+380'

  const [formData, setFormData] = useState<ContactFormData>({
    fullName: "",
    phone: phoneDefault,
    premiseType: "",
    otherPremiseType: "",
    buildingType: "",
    needsDemolition: "",
    demolitionDetails: "",
    hasDesignProject: "",
    startDate: "",
    endDate: "",
    additionalComment: "",
    priorities: [],
  })
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [submitStatus, setSubmitStatus] = useState<"idle" | "success" | "error">("idle")
  const [statusMessage, setStatusMessage] = useState<string>("")
  const [errors, setErrors] = useState<Partial<ContactFormData>>({})
  const formRef = useRef<HTMLFormElement>(null)

  // Update phone prefix when language changes (only if still at a default value)
  useEffect(() => {
    setFormData((prev) => {
      if (prev.phone === '+380' || prev.phone === '+33') {
        return { ...prev, phone: lang === 'fr' ? '+33' : '+380' }
      }
      return prev
    })
  }, [lang])

  const validatePhone = (phone: string): boolean => {
    if (lang === 'fr') {
      return /^\+\d{7,15}$/.test(phone.trim())
    }
    return /^\+380\d{9}$/.test(phone.trim())
  }

  const validateForm = (): boolean => {
    const newErrors: Partial<ContactFormData> = {}
    if (!formData.fullName.trim()) {
      newErrors.fullName = t("formError1Required")
    } else if (formData.fullName.trim().length < 2) {
      newErrors.fullName = t("formError1Short")
    }
    const phoneEmpty = !formData.phone.trim() || formData.phone === '+380' || formData.phone === '+33'
    if (phoneEmpty) {
      newErrors.phone = t("formPhoneErrorRequired")
    } else if (!validatePhone(formData.phone)) {
      newErrors.phone = t("formPhoneErrorFormat")
    }
    if (!formData.premiseType) {
      newErrors.premiseType = t("formError3")
    }
    if (formData.premiseType === "other" && !formData.otherPremiseType?.trim()) {
      newErrors.otherPremiseType = t("formOtherPremiseError")
    }
    if (!formData.buildingType) {
      newErrors.buildingType = t("formError4")
    }
    if (!formData.needsDemolition) {
      newErrors.needsDemolition = t("formError5")
    }
    if (formData.needsDemolition === "yes" && !formData.demolitionDetails?.trim()) {
      newErrors.demolitionDetails = t("formDemolitionError")
    }
    if (!formData.hasDesignProject) {
      newErrors.hasDesignProject = t("formError6")
    }
    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const handlePriorityChange = (value: string) => {
    setFormData((prev) => {
      let newPriorities = [...prev.priorities]
      if (newPriorities.includes(value)) {
        newPriorities = newPriorities.filter((p) => p !== value)
      } else {
        if (newPriorities.length >= 2) {
          newPriorities = [newPriorities[1], value]
        } else {
          newPriorities.push(value)
        }
      }
      return { ...prev, priorities: newPriorities }
    })
    if (errors.priorities) {
      setErrors((prev) => ({ ...prev, priorities: undefined }))
    }
    if (submitStatus !== "idle") {
      setSubmitStatus("idle")
      setStatusMessage("")
    }
  }

  const handleInputChange = (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target
    if (name === "phone") {
      let phoneValue = value
      if (lang === 'fr') {
        if (!phoneValue.startsWith("+")) phoneValue = "+" + phoneValue.replace(/^\+?/, "")
        if (phoneValue.length > 16) phoneValue = phoneValue.slice(0, 16)
      } else {
        if (!phoneValue.startsWith("+380")) {
          phoneValue = "+380" + phoneValue.replace(/^\+?380?/, "")
        }
        if (phoneValue.length > 13) phoneValue = phoneValue.slice(0, 13)
      }
      setFormData((prev) => ({ ...prev, [name]: phoneValue }))
    } else {
      setFormData((prev) => ({ ...prev, [name]: value }))
    }
    if (name === "premiseType" && value !== "other") {
      setFormData((prev) => ({ ...prev, otherPremiseType: "" }))
    }
    if (name === "needsDemolition" && value !== "yes") {
      setFormData((prev) => ({ ...prev, demolitionDetails: "" }))
    }
    if (errors[name as keyof ContactFormData]) {
      setErrors((prev) => ({ ...prev, [name]: undefined }))
    }
    if (submitStatus !== "idle") {
      setSubmitStatus("idle")
      setStatusMessage("")
    }
  }

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault()
    if (!validateForm()) {
      const firstErrorField = Object.keys(errors)[0]
      const errorElement = formRef.current?.querySelector(`[name="${firstErrorField}"]`) as HTMLElement
      errorElement?.focus()
      return
    }
    setIsSubmitting(true)
    setSubmitStatus("idle")
    setStatusMessage("")
    try {
      const result = await sendEmailAction(formData)
      if (result.success) {
        setSubmitStatus("success")
        setStatusMessage(result.message)
        setFormData({
          fullName: "",
          phone: phoneDefault,
          premiseType: "",
          otherPremiseType: "",
          buildingType: "",
          needsDemolition: "",
          demolitionDetails: "",
          hasDesignProject: "",
          startDate: "",
          endDate: "",
          additionalComment: "",
          priorities: [],
        })
        announceToScreenReader(result.message, "polite")
      } else {
        setSubmitStatus("error")
        setStatusMessage(result.message)
        announceToScreenReader(result.message, "assertive")
      }
    } catch {
      setSubmitStatus("error")
      setStatusMessage(t("formUnexpectedError"))
      announceToScreenReader(t("formUnexpectedError"), "assertive")
    } finally {
      setIsSubmitting(false)
    }
  }

  const announceToScreenReader = (message: string, priority: "polite" | "assertive" = "polite") => {
    const announcement = document.createElement("div")
    announcement.setAttribute("aria-live", priority)
    announcement.setAttribute("aria-atomic", "true")
    announcement.className = "sr-only"
    announcement.textContent = message
    document.body.appendChild(announcement)
    setTimeout(() => document.body.removeChild(announcement), 1000)
  }

  const getTodayDate = () => new Date().toISOString().split("T")[0]

  const handleSelectorChange = (fieldName: string) => (value: string) => {
    handleInputChange({
      target: { name: fieldName, value },
    } as ChangeEvent<HTMLSelectElement>)
  }

  const priorityOptions = getPriorityOptions(lang)
  const premiseTypes = getPremiseTypes(lang)
  const buildingTypes = getBuildingTypes(lang)
  const demolitionOptions = getDemolitionOptions(lang)
  const designProjectOptions = getDesignProjectOptions(lang)

  return (
    <div>
      <form
        ref={formRef}
        onSubmit={handleSubmit}
        className="space-y-8"
        noValidate
        aria-label={t("formAriaLabel")}
      >
        {/* 1. Name */}
        <div>
          <label htmlFor="fullName" className="block text-white font-medium mb-3 text-lg">
            {t("formLabel1")} <span className="text-red-400">*</span>
          </label>
          <input
            type="text"
            id="fullName"
            name="fullName"
            value={formData.fullName}
            onChange={handleInputChange}
            required
            aria-required="true"
            aria-invalid={errors.fullName ? "true" : "false"}
            aria-describedby={errors.fullName ? "fullName-error" : undefined}
            className={`bg-black/50 font-light w-full px-4 py-3 border rounded-lg text-white placeholder-gray-400 transition-colors focus:outline-none focus:ring-1 focus:ring-gray-900 focus:ring-offset-1 focus:ring-offset-gray-900 ${
              errors.fullName ? "border-red-500" : "border-gray-700 focus:border-white"
            }`}
            placeholder={t("formPlaceholder1")}
          />
          {errors.fullName && (
            <p id="fullName-error" className="font-light mt-2 text-red-400 text-sm flex items-center" role="alert" aria-live="polite">
              <AlertCircle className="w-4 h-4 mr-1" aria-hidden="true" />
              {errors.fullName}
            </p>
          )}
        </div>

        {/* 2. Phone */}
        <div>
          <label htmlFor="phone" className="block text-white font-medium mb-3 text-lg">
            {t("formLabel2")} <span className="text-red-400">*</span>
          </label>
          <div className="relative">
            <input
              type="tel"
              id="phone"
              name="phone"
              value={formData.phone}
              onChange={handleInputChange}
              required
              autoComplete="tel"
              aria-required="true"
              aria-invalid={errors.phone ? "true" : "false"}
              aria-describedby={errors.phone ? "phone-error" : undefined}
              className={`bg-black/50 font-light w-full px-4 py-3 border rounded-lg text-white placeholder-gray-400 transition-colors focus:outline-none focus:ring-1 focus:ring-gray-900 focus:ring-offset-1 focus:ring-offset-gray-900 ${
                errors.phone ? "border-red-500" : "border-gray-700 focus:border-white"
              }`}
              placeholder={t("formPhonePlaceholder")}
            />
          </div>
          {errors.phone && (
            <p id="phone-error" className="font-light mt-2 text-red-400 text-sm flex items-center" role="alert" aria-live="polite">
              <AlertCircle className="w-4 h-4 mr-1" aria-hidden="true" />
              {errors.phone}
            </p>
          )}
        </div>

        {/* 3. Premise type */}
        <div>
          <label htmlFor="premiseType" className="block text-white font-medium mb-3 text-lg">
            {t("formLabel3")} <span className="text-red-400">*</span>
          </label>
          <div className="relative">
            <Selector
              id="premiseType"
              name="premiseType"
              value={formData.premiseType}
              onChange={handleSelectorChange('premiseType')}
              required
              options={premiseTypes}
              aria-required="true"
              aria-invalid={errors.premiseType ? "true" : "false"}
              aria-describedby={errors.premiseType ? "premiseType-error" : undefined}
              className={`font-light w-full px-4 py-3 bg-black/50 border rounded-lg text-white appearance-none cursor-pointer transition-colors focus:outline-none focus:ring-1 focus:ring-gray-900 focus:ring-offset-1 focus:ring-offset-gray-900 ${
                errors.premiseType ? "border-red-500" : "border-gray-700 focus:border-white"
              } ${!formData.premiseType ? "text-gray-400" : "text-white"}`}
            />
          </div>
          {formData.premiseType === "other" && (
            <div className="mt-3">
              <textarea
                id="otherPremiseType"
                name="otherPremiseType"
                value={formData.otherPremiseType}
                onChange={handleInputChange}
                rows={3}
                placeholder={t("formOtherPremisePlaceholder")}
                className={`font-light w-full px-4 py-3 max-h-24 min-h-24 bg-black/50 border rounded-lg text-white placeholder-gray-400 transition-colors focus:outline-none focus:ring-1 focus:ring-gray-900 focus:ring-offset-1 focus:ring-offset-gray-900 resize-vertical ${
                  errors.otherPremiseType ? "border-red-500" : "border-gray-700 focus:border-white"
                }`}
              />
            </div>
          )}
          {errors.premiseType && (
            <p id="premiseType-error" className="font-light mt-2 text-red-400 text-sm flex items-center" role="alert" aria-live="polite">
              <AlertCircle className="w-4 h-4 mr-1" aria-hidden="true" />
              {errors.premiseType}
            </p>
          )}
          {errors.otherPremiseType && (
            <p className="font-light mt-2 text-red-400 text-sm flex items-center" role="alert" aria-live="polite">
              <AlertCircle className="w-4 h-4 mr-1" aria-hidden="true" />
              {errors.otherPremiseType}
            </p>
          )}
        </div>

        {/* 4. Building type */}
        <div>
          <label htmlFor="buildingType" className="block text-white font-medium mb-3 text-lg">
            {t("formLabel4")} <span className="text-red-400">*</span>
          </label>
          <div className="relative">
            <Selector
              id="buildingType"
              name="buildingType"
              value={formData.buildingType}
              onChange={handleSelectorChange('buildingType')}
              required
              options={buildingTypes}
              aria-required="true"
              aria-invalid={errors.buildingType ? "true" : "false"}
              aria-describedby={errors.buildingType ? "buildingType-error" : undefined}
              className={`font-light w-full px-4 py-3 bg-black/50 border rounded-lg text-white appearance-none cursor-pointer transition-colors focus:outline-none focus:ring-1 focus:ring-gray-900 focus:ring-offset-1 focus:ring-offset-gray-900 ${
                errors.buildingType ? "border-red-500" : "border-gray-700 focus:border-white"
              } ${!formData.buildingType ? "text-gray-400" : "text-white"}`}
            />
          </div>
          {errors.buildingType && (
            <p id="buildingType-error" className="font-light mt-2 text-red-400 text-sm flex items-center" role="alert" aria-live="polite">
              <AlertCircle className="w-4 h-4 mr-1" aria-hidden="true" />
              {errors.buildingType}
            </p>
          )}
        </div>

        {/* 5. Demolition */}
        <div>
          <label htmlFor="needsDemolition" className="block text-white font-medium mb-3 text-lg">
            {t("formLabel5")} <span className="text-red-400">*</span>
          </label>
          <div className="relative">
            <Selector
              id="needsDemolition"
              name="needsDemolition"
              value={formData.needsDemolition}
              onChange={handleSelectorChange('needsDemolition')}
              required
              options={demolitionOptions}
              aria-required="true"
              aria-invalid={errors.needsDemolition ? "true" : "false"}
              aria-describedby={errors.needsDemolition ? "needsDemolition-error" : undefined}
              className={`font-light w-full px-4 py-3 bg-black/50 border rounded-lg text-white appearance-none cursor-pointer transition-colors focus:outline-none focus:ring-1 focus:ring-gray-900 focus:ring-offset-1 focus:ring-offset-gray-900 ${
                errors.needsDemolition ? "border-red-500" : "border-gray-700 focus:border-white"
              } ${!formData.needsDemolition ? "text-gray-400" : "text-white"}`}
            />
          </div>
          {formData.needsDemolition === "yes" && (
            <div className="mt-3">
              <textarea
                name="demolitionDetails"
                value={formData.demolitionDetails || ""}
                onChange={handleInputChange}
                rows={3}
                placeholder={t("formDemolitionPlaceholder")}
                className={`font-light w-full px-4 py-3 max-h-24 min-h-24 bg-black/50 border rounded-lg text-white placeholder-gray-400 transition-colors focus:outline-none focus:ring-1 focus:ring-gray-900 focus:ring-offset-1 focus:ring-offset-gray-900 resize-vertical ${
                  errors.demolitionDetails ? "border-red-500" : "border-gray-700 focus:border-white"
                }`}
              />
            </div>
          )}
          {errors.needsDemolition && (
            <p id="needsDemolition-error" className="font-light mt-2 text-red-400 text-sm flex items-center" role="alert" aria-live="polite">
              <AlertCircle className="w-4 h-4 mr-1" aria-hidden="true" />
              {errors.needsDemolition}
            </p>
          )}
          {errors.demolitionDetails && (
            <p className="font-light mt-2 text-red-400 text-sm flex items-center" role="alert" aria-live="polite">
              <AlertCircle className="w-4 h-4 mr-1" aria-hidden="true" />
              {errors.demolitionDetails}
            </p>
          )}
        </div>

        {/* 6. Design project */}
        <div>
          <label htmlFor="hasDesignProject" className="block text-white font-medium mb-3 text-lg">
            {t("formLabel6")} <span className="text-red-400">*</span>
          </label>
          <div className="relative">
            <Selector
              id="hasDesignProject"
              name="hasDesignProject"
              value={formData.hasDesignProject}
              onChange={handleSelectorChange('hasDesignProject')}
              required
              options={designProjectOptions}
              aria-required="true"
              aria-invalid={errors.hasDesignProject ? "true" : "false"}
              aria-describedby={errors.hasDesignProject ? "hasDesignProject-error" : undefined}
              className={`font-light w-full px-4 py-3 bg-black/50 border rounded-lg text-white appearance-none cursor-pointer transition-colors focus:outline-none focus:ring-1 focus:ring-gray-900 focus:ring-offset-1 focus:ring-offset-gray-900 ${
                errors.hasDesignProject ? "border-red-500" : "border-gray-700 focus:border-white"
              } ${!formData.hasDesignProject ? "text-gray-400" : "text-white"}`}
            />
          </div>
          {errors.hasDesignProject && (
            <p id="hasDesignProject-error" className="font-light mt-2 text-red-400 text-sm flex items-center" role="alert" aria-live="polite">
              <AlertCircle className="w-4 h-4 mr-1" aria-hidden="true" />
              {errors.hasDesignProject}
            </p>
          )}
        </div>

        {/* 7 & 8 & 9 */}
        <div>
          {/* 7. Priorities */}
          <div className="mb-4">
            <div className="block text-white font-medium mb-0 text-lg">
              {t("formLabel7")} <span className="text-red-400">*</span>
            </div>
            <p className="text-gray-400 text-sm mb-4">{t("formLabel7Sub")}</p>
            <div className="space-y-3">
              {priorityOptions.map((option) => {
                const inputId = `priority-${option.value}`
                return (
                  <label key={option.value} className="flex items-start space-x-3 cursor-pointer group">
                    <div className="relative flex items-center justify-center mt-1">
                      <input
                        id={inputId}
                        name="priorities"
                        type="checkbox"
                        checked={formData.priorities.includes(option.value)}
                        onChange={() => handlePriorityChange(option.value)}
                        className="sr-only"
                      />
                      <div className={`w-5 h-5 border-2 rounded transition-all duration-200 flex items-center justify-center ${
                        formData.priorities.includes(option.value)
                          ? "bg-white border-white"
                          : "border-gray-400 group-hover:border-gray-300"
                      }`}>
                        {formData.priorities.includes(option.value) && <Check className="w-3 h-3 text-black" />}
                      </div>
                    </div>
                    <div className="flex-1">
                      <span className="text-white group-hover:text-gray-300 transition-colors">{option.label}</span>
                    </div>
                  </label>
                )
              })}
            </div>
          </div>

          {/* 8. Dates */}
          <div className="mb-4">
            <div className="block text-white font-medium mb-4 text-lg">
              {t("formLabel8")}
            </div>
            <div className="grid md:grid-cols-2 gap-6">
              <div>
                <label htmlFor="startDate" className="block text-gray-300 mb-3 font-light">
                  {t("formDateStart")}
                </label>
                <div className="relative">
                  <input
                    type="date"
                    id="startDate"
                    name="startDate"
                    value={formData.startDate}
                    onChange={handleInputChange}
                    min={getTodayDate()}
                    className="font-light w-full px-4 py-3 bg-black/50 border border-gray-700 rounded-lg text-white focus:border-white focus:outline-none focus:ring-1 focus:ring-gray-900 focus:ring-offset-1 focus:ring-offset-gray-900 transition-colors cursor-pointer [&::-webkit-calendar-picker-indicator]:invert [&::-webkit-calendar-picker-indicator]:cursor-pointer"
                    placeholder="_ / _ / 20_"
                  />
                </div>
              </div>
              <div>
                <label htmlFor="endDate" className="block text-gray-300 mb-3 font-light">
                  {t("formDateEnd")}
                </label>
                <div className="relative">
                  <input
                    type="date"
                    id="endDate"
                    name="endDate"
                    value={formData.endDate}
                    onChange={handleInputChange}
                    min={formData.startDate || getTodayDate()}
                    className="font-light w-full px-4 py-3 bg-black/50 border border-gray-700 rounded-lg text-white focus:border-white focus:outline-none focus:ring-1 focus:ring-gray-900 focus:ring-offset-1 focus:ring-offset-gray-900 transition-colors cursor-pointer [&::-webkit-calendar-picker-indicator]:invert [&::-webkit-calendar-picker-indicator]:cursor-pointer"
                    placeholder="_ / _ / 20___"
                  />
                </div>
              </div>
            </div>
          </div>

          {/* 9. Comment */}
          <div>
            <label htmlFor="additionalComment" className="block text-white font-medium mb-3 text-lg">
              {t("formLabel9")}
            </label>
            <textarea
              id="additionalComment"
              name="additionalComment"
              value={formData.additionalComment}
              onChange={handleInputChange}
              rows={4}
              className="max-h-24 min-h-24 font-light w-full px-4 py-3 bg-black/50 border border-gray-700 rounded-lg text-white placeholder-gray-400 focus:border-white focus:outline-none focus:ring-1 focus:ring-gray-900 focus:ring-offset-1 focus:ring-offset-gray-900 transition-colors resize-vertical"
              placeholder={t("formCommentPlaceholder")}
            />
          </div>

          {/* Submit */}
          <button
            type="submit"
            disabled={isSubmitting}
            className="w-full px-8 py-4 bg-white text-black font-medium rounded-lg hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed transition-colors text-lg focus:outline-none focus:ring-1 focus:ring-gray-900 focus:ring-offset-1 focus:ring-offset-gray-900 flex items-center justify-center"
          >
            {isSubmitting ? (
              <>
                <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-black mr-2" aria-hidden="true" />
                <span>{t("formSubmitting")}</span>
                <span className="sr-only">{t("formSubmittingAria")}</span>
              </>
            ) : (
              t("formSubmit")
            )}
          </button>

          <p className="text-gray-500 text-xs font-light text-center leading-relaxed">
            {t("formPrivacyText")}{" "}
            <Link href="/privacy-policy" className="text-gray-400 underline underline-offset-2 hover:text-white transition-colors">
              {t("formPrivacyLink")}
            </Link>
          </p>

          {submitStatus === "success" && (
            <div className="mt-2 p-4 bg-green-900/50 border border-green-700 rounded-lg flex items-start" role="alert" aria-live="polite">
              <CheckCircle className="w-5 h-5 text-green-400 mr-3 mt-0.5 flex-shrink-0" aria-hidden="true" />
              <div>
                <p className="text-green-300 font-medium">{t("formSuccessTitle")}</p>
                <p className="text-green-300 text-sm mt-1">{statusMessage}</p>
              </div>
            </div>
          )}
          {submitStatus === "error" && (
            <div className="p-4 bg-red-900/50 border border-red-700 rounded-lg flex items-start" role="alert" aria-live="assertive">
              <AlertCircle className="w-5 h-5 text-red-400 mr-3 mt-0.5 flex-shrink-0" aria-hidden="true" />
              <div>
                <p className="text-red-300 font-medium">{t("formErrorTitle")}</p>
                <p className="text-red-300 text-sm mt-1">{statusMessage}</p>
              </div>
            </div>
          )}
          {errors.priorities && (
            <p className="mt-2 text-red-400 text-sm flex items-center" role="alert" aria-live="polite">
              <AlertCircle className="w-4 h-4 mr-1" aria-hidden="true" />
              {errors.priorities}
            </p>
          )}
        </div>
      </form>
    </div>
  )
}

export default Form
